import React, { useEffect, useRef, useState } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import { useLocation, useNavigate } from 'react-router';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { useContext } from 'react';
import { store } from 'components/Store';
import { useAuth0 } from '@auth0/auth0-react';
import { useElectionMenuItems } from 'components/Temporary/Election2024';
import ManifestoComparison from 'components/Temporary/Manifestos/ManifestoComparison';

const useManifestosSearch = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state ?? {};
  const { subscriptionStatus } = activePlan ?? {};
  const { generalApiCall } = useGeneralApiCall();
  const { isAuthenticated } = useAuth0();

  const searchManifestos = async ({ value, manifestosSelected, election2026, source }) => {
    try {
      const subscribedPlan =
        isAuthenticated && (subscriptionStatus === 'Subscribed' || subscriptionStatus === 'OnFreePlan');
      const pathname = `/api/manifesto/${subscribedPlan ? 'signedin-compare' : 'compare'}`;
      const method = 'post';
      const requestProperties = {
        interest: value,
        manifestos: manifestosSelected?.map((item) => `_${election2026 ? 2026 : 2024}_${item.value ?? item.label}`),
        format: 'html',
      };
      const results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        returnError: true,
        needsAuthentication: subscribedPlan,
        requestSource: source,
        notShowErrorMessage: true,
      });
      if (results?.status === 429) {
        return { message: 'limit', suscribed: isAuthenticated };
      } else {
        if (!!results) {
          return results;
        }
      }
    } catch (error) {}
  };
  return { searchManifestos };
};

const ManifestosSearch = (props) => {
  const { comparisonState, openState, isLoading, setIsLoading, election2026TabState } = props;
  const { comparison, setComparison } = comparisonState ?? {};
  const { election2026Tab } = election2026TabState ?? {};
  const { setIsOpen } = openState ?? {};

  const location = useLocation();
  const navigate = useNavigate();
  const { election2026, manifestosData } = useElectionMenuItems();
  const manifestosToRender = election2026 ? manifestosData[election2026Tab] : manifestosData;

  const buildHash = (parties) => {
    const partiesHash = parties.map((party) => party.hash).join('~');
    return election2026 ? `#${election2026Tab}:${partiesHash}` : `#${partiesHash}`;
  };

  const getPartiesFromHash = () => {
    let parties = [];
    let rawHash = location.hash?.replace('#', '') ?? '';
    if (election2026 && rawHash.includes(':')) {
      rawHash = rawHash.split(':')[1];
    }
    let hashParties = rawHash.split('~');
    for (let i = 0; i < hashParties.length; i++) {
      let party = manifestosToRender.find((item) => item.hash === hashParties[i]);
      if (party) {
        parties = [...parties, party];
      }
    }
    return parties.length > 0 ? parties : manifestosToRender.filter((item) => item.default);
  };

  const [manifestosSelected, setManifestosSelected] = useState(getPartiesFromHash());
  const [searchValue, setSearchValue] = useState('');

  const isSmallMobile = useIsMobile(480);

  const inputSearch = useRef(null);
  const [heightContainer, containerRef] = useHeightContainer();
  const { searchManifestos } = useManifestosSearch;

  const interestQuery = getUrlParam('interest');
  const searchResults = async (value = searchValue) => {
    try {
      if (value.trim() === '') {
        setComparison(null);
      } else {
        setIsLoading(true);
        const results = await searchManifestos({ value, manifestosSelected, election2026 });
        if (results?.message === 'limit') {
          if (results?.suscribed) {
            createNotification('danger', 'You have reached the limit of 15 searches in the past 24 hours');
          } else {
            setIsOpen(true);
          }
          setIsLoading(false);
          setComparison(null);
        } else if (!!results) {
          setComparison(results);
          setIsLoading(false);
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (inputSearch?.current && searchValue === '' && !isSmallMobile) {
      inputSearch?.current?.focus();
    }
  }, [comparison]);

  useEffect(() => {
    if (interestQuery && comparison === null) {
      const interestVal = decodeUrlQueryValue(interestQuery);
      if (searchValue === '') {
        setSearchValue(interestVal);
      }
      searchResults(interestVal);
    } else {
      if (comparison != null) {
        setComparison(null);
        setSearchValue('');
      }
    }
  }, [interestQuery]);

  useEffect(() => {
    if (election2026) {
      setManifestosSelected(getPartiesFromHash());
      navigate({ hash: buildHash(getPartiesFromHash()) }, { replace: true });
    }
  }, [election2026Tab]);

  const setSearchQuery = () => {
    let urlToHistory = `${location.pathname}?interest=${encodeURIComponent(searchValue.trim())}${buildHash(manifestosSelected)}`;
    navigate(urlToHistory, { replace: true });
  };

  return (
    <>
      {isLoading ? (
        <div
          ref={containerRef}
          style={{ minHeight: `${heightContainer}px` }}
          className='p-lg-5 py-4 px-3 d-flex flex-column justify-content-center'
        >
          <div>
            <div className='text-center pb-5 mx-auto waiting-screen'>
              <>
                <p className='mb-2'>
                  <strong>{`PolicyMogul is analysing manifestos for you`}</strong>
                </p>
                <p>This typically takes 10 - 20 seconds, but may take longer</p>
              </>
            </div>
            <div className='waiting-bar mx-auto'>
              <div className='waiting-bar-indicator' />
            </div>
          </div>
        </div>
      ) : (
        <div className='dashboard-root-container'>
          {!!comparison ? (
            <>
              <ManifestoComparison
                searchValue={searchValue}
                setComparison={setComparison}
                manifestosSelected={manifestosSelected}
                comparison={comparison}
              />
            </>
          ) : (
            <>
              <div className='border-bottom py-4 px-3 px-lg-5'>
                <div>
                  <label className='title-h5-m-bold mb-1 d-block'>Choose manifestos to compare</label>
                  <div className='flex-centered flex-wrap'>
                    {manifestosToRender.map((item) => {
                      const { label, selectable } = item;
                      return (
                        <div key={`party-${label}`} className='common-outside-filter manifesto-filter'>
                          <div
                            className={`checkbox-item position-relative mb-0 ${!selectable ? 'checkbox-item-disabled' : ''}`}
                          >
                            <input
                              id={`${normalize(label)}`}
                              onChange={() => {
                                if (selectable) {
                                  let newItems = [...manifestosSelected];
                                  let itemPosition = newItems.findIndex((item) => item.label === label);
                                  let newParties = [];
                                  if (itemPosition >= 0) {
                                    newItems.splice(itemPosition, 1);
                                    newParties = [...newItems];
                                  } else {
                                    newParties = [...manifestosSelected, item];
                                  }
                                  if (newParties.length >= 1 && newParties.length <= 3) {
                                    setManifestosSelected(newParties);
                                    navigate(
                                      {
                                        search: window.location.search,
                                        hash: buildHash(newParties),
                                      },
                                      { replace: true }
                                    );
                                  } else if (newParties.length < 1) {
                                    createNotification('danger', 'You must have at least 1 party selected');
                                  } else if (newParties.length > 3) {
                                    createNotification('danger', 'You can compare up to three manifestos at a time');
                                  }
                                }
                              }}
                              type={'checkbox'}
                              checked={!!manifestosSelected.find((item) => item?.label === label)}
                              disabled={!selectable}
                            />
                            <label htmlFor={normalize(label)} className={`create-email-alert-label font-weight-normal`}>
                              <p className='mb-0 line-height-1-5'>{label}</p>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className='bg-blue-hovered py-4 px-3 px-lg-5 flex-grow-1'>
                <div>
                  <label className='title-h5-m-bold mb-3 d-block'>Describe your interests</label>
                  <textarea
                    ref={inputSearch}
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setSearchQuery();
                        searchResults();
                      }
                    }}
                    className='writing-textarea p-3 border rounded'
                    placeholder='Tell us about one of your policy interests using natural language '
                  />
                  <div className='text-right pt-4'>
                    <button
                      onClick={() => {
                        setSearchQuery();
                        searchResults();
                      }}
                      className='action-button px-2 px-lg-4 rounded'
                      disabled={searchValue === ''}
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export { useManifestosSearch };
export default ManifestosSearch;
